// frontend/src/components/Common/Comments/CommentItem.jsx

import React, { useState, useEffect } from 'react';
import { commentsAPI, attachmentsAPI } from '../../../api/client';
import { useTranslation } from '../../../context/TranslationContext';
import { FiLock, FiUser, FiPaperclip, FiDownload, FiTrash2, FiTag } from 'react-icons/fi';
import RichTextEditor from '../RichTextEditor';
import './Comments.css';

const CommentItem = ({ comment, onViewed, currentUserId, hideSubEntity = false }) => {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(true);  

  // Simplified: Mark as viewed after a short delay when component mounts
  // Mark as viewed after 1 second
  useEffect(() => {
    // API returns is_viewed (not is_read) — only mark comments not yet seen
    if (!comment || comment.is_viewed) return;

    const timer = setTimeout(() => {
      markAsViewed();
    }, 1000);

    return () => clearTimeout(timer);
  }, [comment?.comment_id, comment?.is_viewed]);


  // Load attachments using existing API
  useEffect(() => {
    if (comment?.comment_id) {
      loadAttachments();
    }
  }, [comment?.comment_id]);
  

  const loadAttachments = async () => {
    try {
      setLoadingAttachments(true);
      const response = await attachmentsAPI.getAttachments('comment', comment.comment_id);
      if (response.data.success) {
        setAttachments(response.data.attachments);  // Note: 'attachments' not 'data'
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
    } finally {
      setLoadingAttachments(false);
    }
  };


  const markAsViewed = async () => {
    try {
      await commentsAPI.markAsViewed(comment.comment_id);
      onViewed(comment.comment_id);
    } catch (error) {
      console.error('Error marking comment as viewed:', error);
    }
  };


  // Download using existing API
const handleDownloadAttachment = (attachmentId, fileName) => {
  // attachmentsAPI.download() returns a token-authenticated URL
  const url = attachmentsAPI.download(attachmentId);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => link.remove(), 100);
};



  // Delete using existing API
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm(t('comments.confirm_delete_attachment', 'Delete this attachment?'))) {
      return;
    }

    try {
      await attachmentsAPI.delete(attachmentId);
      setAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('comments.just_now', 'Just now');
    if (diffMins < 60) return `${diffMins}${t('comments.min_ago', 'm ago')}`;
    if (diffHours < 24) return `${diffHours}${t('comments.hour_ago', 'h ago')}`;
    if (diffDays < 7) return `${diffDays}${t('comments.day_ago', 'd ago')}`;
    
    return date.toLocaleDateString();
  };

  if (!comment) {
    return null;
  }

    // Get current user ID safely (might be undefined if not passed)
  const isCurrentUserComment = currentUserId && comment.created_by === currentUserId;

  // Mentioned users to show in the header — sole source now that
  // destination_user_id has been folded into mentioned_user_ids.
  const mentionedList = Array.isArray(comment.mentioned_users) ? comment.mentioned_users : [];

  return (
    <div 
      className={`comment-item ${!comment.is_viewed ? 'comment-unread' : ''} ${!comment.is_public ? 'comment-private' : ''}`}
    >
      {/* Unread indicator */}
      {!comment.is_viewed && <div className="comment-unread-indicator" />}

      {/* Header */}
      <div className="comment-item-header">
        <div className="comment-author">
          <div className="comment-avatar">
            {comment.created_by_name ? comment.created_by_name.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="comment-author-info">
            <span className="comment-author-name">
              {comment.created_by_name || comment.created_by_username}
            </span>
            {mentionedList.length > 0 && (
              <span className="comment-destination">
                → <FiUser size={12} />{' '}
                {mentionedList.map(u => u.full_name || u.username).join(', ')}
              </span>
            )}
          </div>
        </div>
        
        <div className="comment-meta">
          {!comment.is_public && (
            <span className="comment-private-badge" title={t('comments.private', 'Private')}>
              <FiLock size={12} />
            </span>
          )}
          <span className="comment-time">{formatDate(comment.created_at)}</span>
        </div>
      </div>

      {/* Task reference — shows which operation this comment is about */}
      {!hideSubEntity && comment.sub_entity_type && (
        <div className="comment-sub-entity" title={comment.sub_entity_label || ''}>
          <FiTag size={11} />
          <span>{comment.sub_entity_label || `${comment.sub_entity_type} #${comment.sub_entity_id}`}</span>
        </div>
      )}

      {/* Rich text content */}
      <RichTextEditor
        value={comment.comment_text || ''}
        readOnly
        minHeight={0}
      />


      {/* File Attachments */}
      {attachments.length > 0 && (
        <div className="comment-attachments">
          {attachments.map(attachment => (
            <div 
              key={attachment.attachment_id} 
              className="attachment-item"
              onClick={() => handleDownloadAttachment(attachment.attachment_id, attachment.original_filename)}
              style={{ cursor: 'pointer' }}
            >
              <FiPaperclip size={14} />
              <span className="attachment-name">{attachment.original_filename}</span>
              <span className="attachment-size">
                ({(attachment.file_size / 1024).toFixed(0)} KB)
              </span>
              <div className="attachment-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleDownloadAttachment(attachment.attachment_id, attachment.original_filename)}
                  className="btn-attachment-action"
                  title={t('download', 'Download')}
                >
                  <FiDownload size={14} />
                </button>
                {isCurrentUserComment && (
                  <button
                    onClick={() => handleDeleteAttachment(attachment.attachment_id)}
                    className="btn-attachment-action btn-delete"
                    title={t('delete', 'Delete')}
                  >
                    <FiTrash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}


    </div>
  );
};

export default CommentItem;